import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

import { isSlideComponentDrawing, Question } from '@/types/slide';
import { ResultContainer, ResultContent, ResultTitle } from '../ResultCard';
import { Box, Stack, Typography } from '@mui/material';
import { ResultCardUnsupported } from './ResultCardUnsupported';
import { useTranslation } from 'react-i18next';
import { formatDuration } from '../../format';
import { isCorrectnessDeterminable } from '@/utils/score';
import { ResultDisplayDrawing } from '@/components/results/ResultDisplayDrawing';

export const ResultCardDrawing: React.FC<{ question: Question }> = ({ question }) => {
    const { t } = useTranslation();

    if (!isSlideComponentDrawing(question)) {
        return <ResultCardUnsupported question={question} />;
    }

    // caption and time spent
    const caption = question.answers.length === 0 ? t('result.message.noResult') : '';
    const timeSpent = formatDuration(question.answers.reduce((total, answer) => total + (new Date(answer.endTime).getTime() - new Date(answer.startTime).getTime()), 0) / 1000);

    // only care the latest answer
    const answer = question.answers[0];
    const scored = !isCorrectnessDeterminable(question) || answer?.correct;
    const background = question.content.imageURL ? question.content.imageURL : question.content.image ? `/asset/characters/${question.content.image}/image.png` : undefined;

    return (
        <>
            <ResultContainer>
                <Stack spacing={1}>
                    <Box sx={{ display: 'flex' }}>
                        <ResultTitle variant='h5'>{t('slide.components.drawing.title')}</ResultTitle>
                        <Box sx={{ flexGrow: 1 }} />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography color={question.answers.length > 0 ? 'text.secondary' : 'text.warning'}>{caption}</Typography>
                        {question.answers.length > 0 && (
                            <Typography color='text.secondary'>
                                {t('result.label.timeSpent')}: {timeSpent}
                            </Typography>
                        )}
                    </Box>
                </Stack>
                <ResultContent>
                    <Box sx={{ px: 0.5 }}>
                        <Box
                            sx={{
                                width: question.size.width,
                                height: question.size.height,
                                outline: 4,
                                outlineColor: (theme) => (theme.vars || theme).palette.grey[500],
                                borderRadius: 1,
                                backgroundColor: 'white',
                                position: 'relative',
                            }}
                        >
                            {background && (
                                <Box
                                    component='img'
                                    alt='image'
                                    src={background}
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        aspectRatio: 'auto',
                                        objectFit: 'contain',
                                    }}
                                />
                            )}
                            <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
                                <ResultDisplayDrawing question={question} />
                            </Box>
                        </Box>
                    </Box>
                </ResultContent>
                <Box
                    sx={(theme) => ({
                        backgroundColor: !answer ? 'transparent' : scored ? (theme.vars || theme).palette.success.main : (theme.vars || theme).palette.error.main,
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        p: 1,
                        borderRadius: '4px',
                    })}
                >
                    {!answer ? (
                        <Typography variant='h5' color='warning' textAlign={'center'}>
                            {t('result.label.noResult')}
                        </Typography>
                    ) : scored ? (
                        <CheckIcon style={{ color: 'white' }} />
                    ) : (
                        <CloseIcon style={{ color: 'white' }} />
                    )}
                </Box>
            </ResultContainer>
        </>
    );
};
